import { DOCUMENT } from '@angular/common';
import { Component, effect, inject } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { Title } from '@angular/platform-browser';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';

import { WorkflowTransportService } from '@rpgc-ai/ui/game-runtime';
import { NotificationCardComponent } from '@rpgc-ai/ui/notification-card/notification-card.component';

import { OverlayComponent } from '@rpgc-ai/blocks/overlay/overlay.component';

import { YandexMetricaService } from '@rpgc-ai/services/analytics/yandex-metrica.service';
import { MagicLinkErrorRedirectService } from '@rpgc-ai/services/auth/magic-link-error-redirect.service';
import { PasswordResetDeepLinkService } from '@rpgc-ai/services/auth/password-reset-deep-link.service';
import { SessionService } from '@rpgc-ai/services/auth/session.service';
import { ChatsService } from '@rpgc-ai/services/chats/chats.service';
import { ModalHistorySyncService } from '@rpgc-ai/services/modal/modal-history-sync.service';
import { NotificationsService } from '@rpgc-ai/services/notifications/notifications.service';
import { LanguageService } from '@rpgc-ai/services/translate/language.service';
import { getSeoLanguageCode } from '@rpgc-ai/services/translate/languages';

import { IS_SERVER } from '@rpgc-ai/shared-interfaces/tokens/is-server.token';
import { WINDOW } from '@rpgc-ai/shared-interfaces/tokens/window.token';

import { environment } from '@rpgc-ai/environments';
import { ModalOutletComponent } from '@rpgc-ai/lazy-blocks/modal-outlet/modal-outlet.component';

import { filter, map } from 'rxjs/operators';

import { HreflangService } from './core/services/hreflang.service';

const GOOGLE_ANALYTICS_TAG_ID = 'G-4Y9XNNQC0L';
const GOOGLE_ADS_TAG_ID = 'AW-17995155273';
const YANDEX_METRICA_ID = 105383140;

declare global {
  interface Window {
    gtag?: (...args: unknown[]) => void;
  }
}

@Component({
  imports: [RouterOutlet, OverlayComponent, NotificationCardComponent, ModalOutletComponent],
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrl: './app.component.scss',
})
export class AppComponent {
  readonly #isServer = inject(IS_SERVER);
  readonly #window = inject(WINDOW);
  // Нужна инициализация для работы сервиса, пожалуйста, не удаляйте
  readonly hreflangService = inject(HreflangService);
  readonly #router = inject(Router);
  readonly #title = inject(Title);
  readonly #document = inject(DOCUMENT);
  readonly #sessionService = inject(SessionService);
  readonly #notificationsService = inject(NotificationsService);
  readonly #modalHistorySyncService = inject(ModalHistorySyncService);
  readonly #magicLinkErrorRedirect = inject(MagicLinkErrorRedirectService);
  readonly #passwordResetDeepLink = inject(PasswordResetDeepLinkService);
  readonly #chatsService = inject(ChatsService);
  readonly #languageService = inject(LanguageService);
  readonly #transport = inject(WorkflowTransportService);
  readonly #yandexMetricaService = inject(YandexMetricaService);

  // Variables
  readonly notifications = this.#notificationsService.notifications;
  #lastTrackedPagePath: string | null = null;

  constructor() {
    // получаем токен
    this.#sessionService.loadSession.set(true);
    this.#chatsService.reloadChats();
    this.#transport.configure(environment.API_URL);
    this.#modalHistorySyncService.init();
    this.#magicLinkErrorRedirect.init();
    this.#passwordResetDeepLink.init();
    this.#yandexMetricaService.init(YANDEX_METRICA_ID);
    this.#initGoogleTagRouteTracking();
    this.#syncHtmlLangAttributeEffect();
  }

  // Methods
  removeNotification(id: string): void {
    this.#notificationsService.removeNotification(id);
  }

  #syncHtmlLangAttributeEffect(): void {
    effect(() => {
      this.#document.documentElement.lang = getSeoLanguageCode(this.#languageService.activeLang());
    });
  }

  #initGoogleTagRouteTracking(): void {
    if (this.#isServer) {
      return;
    }

    this.#trackGooglePageView(
      this.#router.url || this.#window!.location.pathname + this.#window!.location.search + this.#window!.location.hash,
    );

    this.#router.events
      .pipe(
        filter((event): event is NavigationEnd => event instanceof NavigationEnd),
        map(({ urlAfterRedirects }) => urlAfterRedirects),
        takeUntilDestroyed(),
      )
      .subscribe((url) => this.#trackGooglePageView(url));
  }

  #trackGooglePageView(pagePath: string): void {
    if (!pagePath || pagePath === this.#lastTrackedPagePath || !this.#window?.gtag) {
      return;
    }

    this.#lastTrackedPagePath = pagePath;

    const pageLocation = this.#window!.location.href;
    const pageTitle = this.#title.getTitle() || this.#document.title;

    this.#window!.gtag?.('event', 'page_view', {
      send_to: GOOGLE_ANALYTICS_TAG_ID,
      page_location: pageLocation,
      page_title: pageTitle,
    });

    this.#window!.gtag?.('event', 'page_view', {
      send_to: GOOGLE_ADS_TAG_ID,
    });
  }
}
